import { Box, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { useColumnVisibilityHook } from 'utils/hooks/tableHooks';
import { renderPartnerLogo } from './partner/renderPartnerLogo';
import { renderPartnerPayoutTypes } from './partner/renderPartnerPayoutTypes';
import { renderPartnerPromotions } from './partner/renderPartnerPromotions';
import { renderPartnerRatings } from './partner/renderPartnerRating';
import { renderPartnerRequirements } from './partner/renderPartnerRequirements';
import { renderCTA } from './renderCTA';
import { renderImage } from './renderImage';
import { renderPrice } from './renderPrice';
import { renderRating } from './renderRating';
import { renderPartnerMasterDetail } from './partner/renderMasterDetail';
import { renderProgress } from './renderProgess';

interface ComparisonTableProps {
  header?: string | null;
  subheader?: string | null;
  tableHeaders: GridColDef[];
  tableRows: GridRowsProp;
  rowHeight?: number | null;
}

const Table = ({
  header,
  subheader,
  tableHeaders,
  tableRows,
  rowHeight,
}: ComparisonTableProps) => {
  const tableHeadersUpdated = tableHeaders.map((tableHeader) => {
    let headers = {};
    if (tableHeader.field === 'image') {
      headers = {
        renderCell: renderImage,
      };
    } else if (tableHeader.field === 'price') {
      headers = {
        renderCell: renderPrice,
        valueGetter: (params: any) => params.row.externalId,
      };
    } else if (tableHeader.field === 'rating') {
      headers = {
        renderCell: renderRating,
      };
    } else if (tableHeader.field === 'cta') {
      headers = {
        align: 'right',
        renderCell: renderCTA,
      };
    } else if (tableHeader.field === 'percentage') {
      headers = {
        renderCell: renderProgress,
        valueGetter: (params: any) => ({
          externalId: params.row.externalId,
          valueToCompareTo: params.row.valueToCompareTo,
        }),
      };
    } else if (tableHeader.field === 'valueToCompareTo') {
      headers = {
        renderCell: ({ value }: any) => (
          <Typography variant="body1">{value.toFixed(2)} €</Typography>
        ),
      };
    } else if (tableHeader.field === 'threeLines') {
      headers = {
        renderCell: ({ value }: any) => (
          <Box sx={{ whiteSpace: 'normal' }}>
            <Typography variant="body2">{value.line1}</Typography>
            <Typography variant="body1">{value.line2}</Typography>
            <Typography variant="body2">{value.line3}</Typography>
          </Box>
        ),
      };
    } else if (tableHeader.field === 'partnerLogo') {
      headers = {
        renderCell: renderPartnerLogo,
        valueGetter: (params: any) => ({ partnerUuid: params.row.partnerUuid }),
      };
    } else if (tableHeader.field === 'partnerRequirements') {
      headers = {
        renderCell: renderPartnerRequirements,
        valueGetter: (params: any) => ({ partnerUuid: params.row.partnerUuid }),
      };
    } else if (tableHeader.field === 'partnerPayoutTypes') {
      headers = {
        renderCell: renderPartnerPayoutTypes,
        valueGetter: (params: any) => ({ partnerUuid: params.row.partnerUuid }),
      };
    } else if (tableHeader.field === 'partnerRating') {
      headers = {
        renderCell: renderPartnerRatings,
        valueGetter: (params: any) => ({ partnerUuid: params.row.partnerUuid }),
      };
    } else if (tableHeader.field === 'partnerPromotions') {
      headers = {
        renderCell: renderPartnerPromotions,
        valueGetter: (params: any) => ({ partnerUuid: params.row.partnerUuid }),
      };
    } else if (tableHeader.field === 'partnerMasterDetail') {
      headers = {
        renderCell: renderPartnerMasterDetail,
        colSpan: (params: any) => {
          if (params.row.partnerMasterDetail) {
            return 6;
          }
          return 1;
        },
        valueGetter: (params: any) => ({
          partnerUuid: params.row.partnerUuid,
          partnerMasterDetail: params.row.partnerMasterDetail,
        }),
      };
    }

    return {
      ...tableHeader,
      ...headers,
      width: tableHeader.width || 100,
      maxWidth: tableHeader.maxWidth || Infinity,
    };
  });
  const columnVisibility = useColumnVisibilityHook(tableHeadersUpdated);

  return (
    <Box sx={{ pt: 5 }}>
      <Typography variant="h4" sx={{ pb: 2, textAlign: 'center' }}>
        {header}
      </Typography>
      <Typography variant="subtitle1" sx={{ pb: 2, textAlign: 'center' }}>
        {subheader}
      </Typography>
      <Box sx={{ pt: 5 }}>
        <DataGrid
          sx={{
            '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
          rows={tableRows}
          columns={tableHeadersUpdated}
          disableSelectionOnClick
          hideFooterPagination={true}
          hideFooter={true}
          autoHeight={true}
          getRowHeight={(params) => 'auto'}
          columnVisibilityModel={{ ...columnVisibility }}
        />
      </Box>
    </Box>
  );
};

export default Table;
